// src/components/modals/LogotipoModal.jsx
import React, { useState } from 'react';
import { FaImage, FaUpload, FaSpinner, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';

const LogotipoModal = ({
    isOpen,
    onClose,
    onUploadLogotipo,
    marca,
    title = "Gestión de Logotipo",
    isEdit = false,
    isOnboarding = false
}) => {
    const [logotipoFile, setLogotipoFile] = useState(null);
    const [logotipoPreview, setLogotipoPreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isOpen) return null;

    const handleLogotipoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validar tipo de archivo
        if (!file.type.startsWith('image/')) {
            Swal.fire({
                title: 'Error',
                text: 'Solo se permiten archivos de imagen (JPEG, PNG, GIF, WebP, SVG)',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        // Validar extensiones específicas
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (!allowedExtensions.includes(fileExtension)) {
            Swal.fire({
                title: 'Error',
                text: 'Formato de archivo no soportado. Use: JPG, PNG, GIF, WebP o SVG',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        // Validar tamaño (5MB máximo)
        if (file.size > 5 * 1024 * 1024) {
            Swal.fire({
                title: 'Error',
                text: 'El archivo es demasiado grande. Máximo 5MB',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        setLogotipoFile(file);

        // Crear preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setLogotipoPreview(e.target.result);
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async () => {
        if (!logotipoFile) {
            Swal.fire({
                title: 'Error',
                text: 'Por favor selecciona un archivo de logotipo',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        if (!marca?._id) {
            Swal.fire({
                title: 'Error',
                text: 'No se pudo identificar la marca',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            await onUploadLogotipo(marca._id, logotipoFile);

            Swal.fire({
                title: '¡Logotipo Actualizado!',
                text: isEdit ? 'El logotipo se ha cambiado exitosamente' : 'El logotipo se ha subido exitosamente',
                icon: 'success',
                confirmButtonText: 'Aceptar',
            });

            handleClose();
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: 'No se pudo subir el logotipo. Por favor, intenta de nuevo.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setLogotipoFile(null);
        setLogotipoPreview(null);
        setIsSubmitting(false);
        onClose();
    };

    return (
        <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
                {/* Fondo oscuro */}
                <div
                    className="fixed inset-0 transition-opacity"
                    aria-hidden="true"
                    onClick={isOnboarding ? undefined : handleClose}
                >
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                {/* Contenedor del modal */}
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md w-full">
                    <div className="px-6 pt-6 pb-4">
                        {/* Header */}
                        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <FaImage className="w-5 h-5 mr-2 text-blue-500" />
                                {title}
                            </h3>
                            {!isOnboarding && (
                                <button
                                    onClick={handleClose}
                                    className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                                >
                                    <FaTimes className="h-5 w-5" />
                                </button>
                            )}
                        </div>

                        {/* Content */}
                        <div className="mt-6 space-y-4">
                            {/* Logotipo actual (si existe) */}
                            {marca?.logotipo && (
                                <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <p className="text-sm text-gray-600 mb-2">Logotipo actual:</p>
                                    <img
                                        src={marca.logotipo.cdnUrl || marca.logotipo.url}
                                        alt={`Logotipo actual de ${marca.nombre}`}
                                        className="w-20 h-20 object-contain border border-gray-200 rounded-lg bg-white mx-auto"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">{marca.logotipo.originalName}</p>
                                </div>
                            )}

                            {/* Área de subida */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {isEdit ? 'Nuevo logotipo' : 'Logotipo de la marca'}
                                </label>
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleLogotipoChange}
                                        className="hidden"
                                        id="logotipo-upload-modal"
                                    />
                                    <label htmlFor="logotipo-upload-modal" className="cursor-pointer">
                                        {logotipoPreview ? (
                                            <div className="space-y-3">
                                                <img
                                                    src={logotipoPreview}
                                                    alt="Nuevo logotipo"
                                                    className="max-w-24 h-auto rounded-lg mx-auto border border-gray-200"
                                                />
                                                <p className="text-sm text-gray-600">
                                                    {logotipoFile?.name}
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setLogotipoFile(null);
                                                        setLogotipoPreview(null);
                                                    }}
                                                    className="text-red-500 hover:text-red-700 text-sm"
                                                >
                                                    Cambiar archivo
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                <FaUpload className="mx-auto h-10 w-10 text-gray-400" />
                                                <div className="text-sm text-gray-600">
                                                    <span className="font-medium text-blue-600 hover:text-blue-500">
                                                        Haz clic para seleccionar
                                                    </span>
                                                    <p>o arrastra y suelta</p>
                                                </div>
                                                <p className="text-xs text-gray-500">
                                                    Formatos: JPG, PNG, GIF, WebP, SVG. Máx: 5MB
                                                </p>
                                            </div>
                                        )}
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Botones */}
                        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={!logotipoFile || isSubmitting}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center"
                            >
                                {isSubmitting ? (
                                    <>
                                        <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                                        {isEdit ? 'Cambiando...' : 'Subiendo...'}
                                    </>
                                ) : (
                                    <>
                                        <FaImage className="w-4 h-4 mr-2" />
                                        {isEdit ? 'Cambiar Logotipo' : 'Subir Logotipo'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LogotipoModal; 